import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DemographicTarget,
  ContentGenre,
  BrandSafetyLevel,
  SafetyFilter,
  TVContentRating,
  getTVContentRatingLabel,
} from "@/types/brand-preferences";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// IAB Audience Demographics - Age Groups
const AGE_GROUPS = [
  { id: DemographicTarget.AGE_18_24, label: "18-24 years" },
  { id: DemographicTarget.AGE_25_34, label: "25-34 years" },
  { id: DemographicTarget.AGE_35_44, label: "35-44 years" },
  { id: DemographicTarget.AGE_45_54, label: "45-54 years" },
  { id: DemographicTarget.AGE_55_64, label: "55-64 years" },
  { id: DemographicTarget.AGE_65_PLUS, label: "65+ years" },
];

// IAB Audience Demographics - Gender
const GENDER_GROUPS = [
  { id: DemographicTarget.MALE, label: "Male" },
  { id: DemographicTarget.FEMALE, label: "Female" },
];


// Content genres
const CONTENT_GENRES = Object.values(ContentGenre).map((value) => ({
  id: value,
  label: value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
}));

// TV Content Ratings
const TV_CONTENT_RATINGS = Object.values(TVContentRating).map((value) => ({
  id: value,
  label: getTVContentRatingLabel(value),
}));

// Safety filters (keeping for backward compatibility)
const SAFETY_FILTERS = Object.values(SafetyFilter).map((value) => ({
  id: value,
  label: value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
}));

// Simplified Form Schema - only fields that are mapped to ranking
const brandPreferencesSchema = z.object({
  profile_name: z.string().min(2, "Campaign name is required"),
  
  // Core fields that affect ranking
  target_demographics: z.array(z.nativeEnum(DemographicTarget)).optional(),
  preferred_genres: z.array(z.nativeEnum(ContentGenre)).optional(),
  
  // Brand safety
  brand_safety_level: z.nativeEnum(BrandSafetyLevel).optional(),
  safety_filters: z.array(z.nativeEnum(SafetyFilter)).optional(),
  
  // TV Content Ratings
  tv_content_ratings: z.array(z.nativeEnum(TVContentRating)).optional(),
  
  // Budget targeting
  target_investment_max: z.coerce.number().optional(),
});

type BrandPreferencesValues = z.infer<typeof brandPreferencesSchema>;

interface BrandPreferencesFormProps {
  initialValues?: Partial<BrandPreferencesValues>;
  onSubmit: (values: BrandPreferencesValues) => Promise<void>;
  isLoading?: boolean;
}

export function BrandPreferencesForm({ initialValues, onSubmit, isLoading }: BrandPreferencesFormProps) {
  const form = useForm<BrandPreferencesValues>({
    resolver: zodResolver(brandPreferencesSchema),
    defaultValues: {
      profile_name: initialValues?.profile_name ?? "",
      target_demographics: initialValues?.target_demographics || [],
      preferred_genres: initialValues?.preferred_genres || [],
      brand_safety_level: initialValues?.brand_safety_level,
      safety_filters: initialValues?.safety_filters || [],
      tv_content_ratings: initialValues?.tv_content_ratings || [],
      target_investment_max: initialValues?.target_investment_max,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        
        {/* Profile Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Campaign Information</h3>
          <FormField
            control={form.control}
            name="profile_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Campaign Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Q3 Awareness Campaign" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Target Demographics - IAB Audience Demographics */}
        <div className="space-y-6 border p-4 rounded-md">
          <h3 className="text-lg font-semibold">Target Demographics</h3>
          
          {/* Age Groups */}
          <FormField
            control={form.control}
            name="target_demographics"
            render={() => (
              <FormItem>
                <FormLabel className="text-base font-medium">Age Groups</FormLabel>
                <div className="grid grid-cols-2 gap-4">
                  {AGE_GROUPS.map((item) => (
                    <FormField
                      key={item.id}
                      control={form.control}
                      name="target_demographics"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={item.id}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(item.id)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...(field.value || []), item.id])
                                    : field.onChange(
                                        field.value?.filter((value) => value !== item.id)
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {item.label}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Gender */}
          <FormField
            control={form.control}
            name="target_demographics"
            render={() => (
              <FormItem>
                <FormLabel className="text-base font-medium">Gender</FormLabel>
                <div className="grid grid-cols-2 gap-4">
                  {GENDER_GROUPS.map((item) => (
                    <FormField
                      key={item.id}
                      control={form.control}
                      name="target_demographics"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={item.id}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(item.id)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...(field.value || []), item.id])
                                    : field.onChange(
                                        field.value?.filter((value) => value !== item.id)
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {item.label}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

        </div>

        {/* Content Preferences */}
        <div className="space-y-4 border p-4 rounded-md">
          <h3 className="text-lg font-semibold">Content Preferences</h3>
          <FormField
            control={form.control}
            name="preferred_genres"
            render={() => (
              <FormItem>
                <FormLabel>Preferred Content Genres</FormLabel>
                <div className="grid grid-cols-2 gap-4">
                  {CONTENT_GENRES.map((genre) => (
                    <FormField
                      key={genre.id}
                      control={form.control}
                      name="preferred_genres"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={genre.id}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(genre.id)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...(field.value || []), genre.id])
                                    : field.onChange(
                                        field.value?.filter((value) => value !== genre.id)
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {genre.label}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* TV Content Ratings */}
        <div className="space-y-4 border p-4 rounded-md">
          <h3 className="text-lg font-semibold">TV Content Ratings</h3>
          <p className="text-sm text-muted-foreground">
            Select the TV content ratings that are acceptable for your brand. These preferences will be mapped to projects based on their content ratings.
          </p>
          
          <FormField
            control={form.control}
            name="tv_content_ratings"
            render={() => (
              <FormItem>
                <FormLabel>Acceptable TV Content Ratings</FormLabel>
                <div className="grid grid-cols-2 gap-4">
                  {TV_CONTENT_RATINGS.map((rating) => (
                    <FormField
                      key={rating.id}
                      control={form.control}
                      name="tv_content_ratings"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={rating.id}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(rating.id)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...(field.value || []), rating.id])
                                    : field.onChange(
                                        field.value?.filter((value) => value !== rating.id)
                                      );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {rating.label}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Budget Targeting */}
        <div className="space-y-4 border p-4 rounded-md">
          <h3 className="text-lg font-semibold">Budget Targeting</h3>
          <FormField
            control={form.control}
            name="target_investment_max"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Maximum Investment Amount (USD)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="e.g. 50000" 
                    {...field}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? "Creating Campaign..." : "Create Campaign"}
        </Button>
      </form>
    </Form>
  );
}
